<template>
  <div class="modern-wrapper">
    <div class="modern-container">
      <!-- Header Section -->
      <div class="header-section">
        <div class="header-content">
          <div class="header-title-container">
            <div class="header-icon">
              <svg 
                width="32" 
                height="32" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
              </svg>
            </div>
            <h1 class="header-title">Receipt Validation Request</h1>
          </div>
          <p class="header-subtitle">Please fill out all required information to validate your receipt. All fields marked with * are mandatory.</p>
        </div>
      </div>
      <!-- Personal Information Section -->
      <div class="section">
        <div class="section-header">
          <svg 
            class="section-icon" 
            width="20" 
            height="20" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            viewBox="0 0 24 24">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle 
              cx="12" 
              cy="7" 
              r="4"/>
          </svg>
          <h3 class="section-title">Personal Information</h3>
        </div>
        <div class="form-grid personal-info-grid">
          <div 
            class="form-group">
            <label class="modern-label">Full Name <span class="required">*</span></label>
            <input
              v-model="contact.name"
              :class="{ 'input-focused': contact.name }"
              placeholder="Enter your full name"
              type="text"
              class="modern-input"
              required
            >
          </div>
          <div class="form-group">
            <label class="modern-label">Email Address</label>
            <input
              v-model="contact.email"
              :class="{ 'input-focused': contact.email }"
              type="email"
              placeholder="Enter your email address"
              class="modern-input"
            >
          </div>
          <div class="form-group full-width">
            <label class="modern-label">Contact Number <span class="required">*</span></label>
            <input
              v-model="contact.contact_no"
              :class="{ 'input-focused': contact.contact_no }"
              type="text"
              placeholder="Enter your contact number"
              class="modern-input"
              required
            >
          </div>
        </div>
      </div>

      <!-- Address Information Section -->
      <div class="section">
        <div class="section-header">
          <svg 
            class="section-icon" 
            width="20" 
            height="20" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            viewBox="0 0 24 24">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle 
              cx="12" 
              cy="10" 
              r="3"/>
          </svg>
          <h3 class="section-title">Address Information</h3>
        </div>
        <div class="form-grid address-info-grid">
          <div class="form-group full-width">
            <label class="modern-label">Address Line 1 <span class="required">*</span></label>
            <input
              v-model="contact.address"
              :class="{ 'input-focused': contact.address }"
              type="text"
              placeholder="Building/Unit number"
              class="modern-input"
              required
            >
          </div>
          <div class="form-group full-width">
            <label class="modern-label">Address Line 2</label>
            <input
              v-model="contact.address_2"
              :class="{ 'input-focused': contact.address_2 }"
              placeholder="Street address"
              class="modern-input"
            >
          </div>
          <div class="form-group full-width">
            <label class="modern-label">Address Line 3</label>
            <input
              v-model="contact.address_3"
              :class="{ 'input-focused': contact.address_3 }"
              placeholder="Area/District (optional)"
              class="modern-input"
            >
          </div>
          <div class="form-group full-width">
            <label class="modern-label">City <span class="required">*</span></label>
            <input
              v-model="contact.city"
              :class="{ 'input-focused': contact.city }"
              placeholder="City Name"
              class="modern-input"
            >
          </div>
          <div class="form-group">
            <label class="modern-label">State <span class="required">*</span></label>
            <select
              id="state"
              v-model="contact.state"
              class="modern-input"
              required
            >
              <option 
                disabled 
                value="">Select state</option>
              <option 
                v-for="state in states" 
                :key="state.Code" 
                :value="state.State"
              >
                {{ state.State }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label class="modern-label">Country <span class="required">*</span></label>
            <select
              id="country"
              v-model="contact.country"
              class="modern-input"
              required
            >
              <option 
                disabled 
                value="">Select country</option>
              <option 
                v-for="country in sortedCountries"
                :key="country.Code"
                :value="country.Country">
                {{ country.Country }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label class="modern-label">Postal Code</label>
            <input
              v-model="contact.postal_zone"
              :class="{ 'input-focused': contact.postal_zone }"
              placeholder="Enter postal code"
              class="modern-input"
            >
          </div>
        </div>
      </div>

      <!-- Identification & Tax Information Section -->
      <div class="section">
        <div class="section-header">
          <svg 
            class="section-icon" 
            width="20" 
            height="20" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            viewBox="0 0 24 24">
            <rect 
              x="1" 
              y="4" 
              width="22" 
              height="16" 
              rx="2" 
              ry="2"/>
            <line 
              x1="1" 
              y1="10" 
              x2="23" 
              y2="10"/>
          </svg>
          <h3 class="section-title">Identification & Tax Information</h3>
        </div>
        <div class="form-grid identification-info-grid">
          <div class="form-group full-width">
            <label 
              for="idType" 
              class="modern-label">Identification Type <span class="required">*</span></label>
            <select
              id="idType"
              v-model="contact.idType"
              class="modern-input"
              required
            >
              <option value="">Select identification type</option>
              <option value="NRIC">Malaysian Individual</option>
              <option value="PASSPORT">Non-Malaysian Individual</option>
              <option value="BRN">Business</option>
            </select>
          </div>
          <div class="form-group full-width">
            <label class="modern-label">
              {{ contact.idType === 'PASSPORT' ? 'Passport Number' : contact.idType === 'BRN' ? 'Business Registration Number' : 'Identification Number' }} <span class="required">*</span>
            </label>
            <input
              v-model="contact.id_no"
              :class="{ 'input-focused': contact.id_no }"
              :placeholder="contact.idType === 'PASSPORT' ? 'Enter your passport number' : contact.idType === 'BRN' ? 'Enter Business Registration No.' : 'Enter your identification number'"
              type="text"
              class="modern-input"
              required
            >
          </div>
          <div class="form-group full-width">
            <label class="modern-label">
              Tax Identification Number (TIN) <span 
                v-if="isBusiness" 
                class="required">*</span>
            </label>
            <input
              v-model="contact.enter_id"
              :class="{ 'input-focused': contact.enter_id }"
              placeholder="Enter TIN (if applicable)"
              type="text"
              class="modern-input"
            >
          </div>
          <!-- SST Registration Number field, visible only for BRN -->
          <div 
            v-if="contact.idType === 'BRN'" 
            class="form-group full-width">
            <div class="label-with-info">
              <label class="modern-label">SST Registration Number <span class="required">*</span></label>
              <span class="info-text">Mandatory if applicable</span>
            </div>
            <input
              v-model="contact.sst_reg_no"
              :class="{ 'input-focused': contact.sst_reg_no }"
              required
              type="text"
              placeholder="Enter SST Reg. No."
              class="modern-input"
              @focus="sstRegFocused = true"
              @blur="sstRegFocused = false"
            >
            <div 
              v-if="sstRegError || sstRegFocused" 
              class="help-text">
              If not applicable, please enter NA
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modern Button Group -->
    <div class="modern-button-group">
      <button 
        class="modern-btn modern-btn-secondary" 
        type="button"
        @click="$emit('previous')">
        <svg 
          width="16" 
          height="16" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2" 
          viewBox="0 0 24 24">
          <polyline points="15,18 9,12 15,6"/>
        </svg>
        Back
      </button>
      <button
        class="modern-btn modern-btn-primary"
        type="button"
        @click="submitData"
      >
        Next
        <svg 
          width="16" 
          height="16" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2" 
          viewBox="0 0 24 24">
          <polyline points="9,18 15,12 9,6"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import stateData from '../../../data/einvoice/state_codes.json';
import countryData from '../../../data/einvoice/country_codes.json';

export default {
    name: 'BuyerInformation',
    props: {
        formData: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            contact: {
                idType: 'NRIC',
                enter_id: '',
                name: '',
                email: '',
                contact_no: '',
                address: '',
                address_2: '',
                address_3: '',
                city: '',
                postal_zone: '',
                state: stateData.length > 0 ? stateData[0].State : '',
                stateCode: stateData.length > 0 ? stateData[0].Code : '',
                country: countryData.length > 0 ? countryData[0].Country : '',
                countryCode: countryData.length > 0 ? countryData[0].Code : '',
                id_no: '',
                sst_reg_no: '', // This field is still in data for logic
            },
            states: stateData,
            countries: countryData,
            sstRegFocused: false,
            sstRegError: false,
        };
    },
    computed: {
        isBusiness() {
            return this.contact.idType === 'BRN';
        },
        sortedCountries() {
            const countries = [...this.countries];
            const malaysiaIndex = countries.findIndex(
                country => country.Code === 'MYS',
            );
            if (malaysiaIndex !== -1) {
                const malaysia = countries.splice(malaysiaIndex, 1)[0];
                countries.unshift(malaysia);
            }
            return countries;
        },
    },
    watch: {
        'contact.sst_reg_no'(newVal) {
            this.sstRegError = !newVal;
        },
        'contact.state'(newVal) {
            // Update state code when state changes
            const selectedState = this.states.find(
                state => state.State === newVal,
            );
            if (selectedState) {
                this.contact.stateCode = selectedState.Code;
            }
        },
        'contact.country'(newVal) {
            // Update country code when country changes
            const selectedCountry = this.countries.find(
                country => country.Country === newVal,
            );
            if (selectedCountry) {
                this.contact.countryCode = selectedCountry.Code;
            }
        },
        contact: {
            deep: true,
            handler() {
                this.$emit('update-form-data', this.contact);
            },
        },
    },
    mounted() {
        if (this.formData) {
            this.contact = {
                ...this.formData,
                address: this.formData.address || '',
                address_2: this.formData.address_2 || '',
                address_3: this.formData.address_3 || '',
                postal_zone: this.formData.postal_zone || '',
                city: this.formData.city || '',
                enter_id: this.formData.enter_id || '',
                sst_reg_no: this.formData.sst_reg_no || '',
            };
        }

        // Initialize state and state code
        if (!this.contact.state && this.states.length > 0) {
            this.contact.state = this.states[0].State;
            this.contact.stateCode = this.states[0].Code;
        } else if (this.contact.state && !this.contact.stateCode) {
            // If state exists but no code, find the code
            const selectedState = this.states.find(
                state => state.State === this.contact.state,
            );
            if (selectedState) {
                this.contact.stateCode = selectedState.Code;
            }
        }

        // Initialize country and country code
        if (!this.contact.country && this.countries.length > 0) {
            const malaysia = this.countries.find(c => c.Code === 'MYS');
            this.contact.country = malaysia
                ? malaysia.Country
                : this.countries[0].Country;
            this.contact.countryCode = malaysia
                ? malaysia.Code
                : this.countries[0].Code;
        } else if (this.contact.country && !this.contact.countryCode) {
            // If country exists but no code, find the code
            const selectedCountry = this.countries.find(
                country => country.Country === this.contact.country,
            );
            if (selectedCountry) {
                this.contact.countryCode = selectedCountry.Code;
            }
        }
    },
    methods: {
        submitData() {
            const missingFields = this.validateForm();
            if (missingFields.length > 0) {
                swal({
                    title: 'Warning',
                    text: `Please fill in the following required fields:\n${missingFields.join(
                        ', ',
                    )}`,
                    type: 'info',
                    confirmButtonColor: '#ef5350',
                    confirmButtonText: 'Close',
                });
                return;
            }

            // Prepare the data with both code and string values
            const submitData = {
                ...this.contact,
                state: this.contact.state || 'NA',
                stateCode: this.contact.stateCode || 'NA',
                address: this.contact.address || 'NA',
                address_2: this.contact.address_2 || 'NA',
                address_3: this.contact.address_3 || 'NA',
                city: this.contact.city || 'NA',
                postal_zone: this.contact.postal_zone || 'NA',
                country: this.contact.country || 'NA',
                countryCode: this.contact.countryCode || 'NA',
            };

            if (!this.contact.enter_id || this.contact.enter_id === 'NA') {
                // Set default TIN based on ID type
                if (this.contact.idType === 'PASSPORT') {
                    submitData.enter_id = 'EI00000000020'; // Non-Malaysian individual
                } else {
                    submitData.enter_id = 'EI00000000010'; // Malaysian individual or business
                }
            }

            this.$emit('submit-data', submitData);
        },
        validateForm() {
            const missingFields = [];

            if (!this.contact.name) {
                missingFields.push('Full Name');
            }
            if (!this.contact.contact_no) {
                missingFields.push('Contact Number');
            } else {
                if (!/^\+6/.test(this.contact.contact_no)) {
                    missingFields.push('Contact Number (must start with +6)');
                } else if (
                    this.contact.contact_no.replace(/\D/g, '').length < 8
                ) {
                    missingFields.push(
                        'Contact Number (must be at least 8 digits)',
                    );
                }
            }

            if (this.contact.email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(this.contact.email)) {
                    missingFields.push('Valid Email Address');
                }
            }
            if (!this.contact.address) missingFields.push('Address Line 1');
            if (!this.contact.city) missingFields.push('City');
            if (!this.contact.state) missingFields.push('State');
            if (!this.contact.country) missingFields.push('Country');

            // Ensure codes are set when state/country are selected
            if (this.contact.state && !this.contact.stateCode) {
                const selectedState = this.states.find(
                    state => state.State === this.contact.state,
                );
                if (selectedState) {
                    this.contact.stateCode = selectedState.Code;
                }
            }
            if (this.contact.country && !this.contact.countryCode) {
                const selectedCountry = this.countries.find(
                    country => country.Country === this.contact.country,
                );
                if (selectedCountry) {
                    this.contact.countryCode = selectedCountry.Code;
                }
            }

            if (!this.contact.idType) missingFields.push('Identification Type');

            if (!this.contact.id_no) {
                const idLabel =
                    this.contact.idType === 'PASSPORT'
                        ? 'Passport Number'
                        : this.contact.idType === 'BRN'
                            ? 'Business Registration Number'
                            : 'Identification Number';
                missingFields.push(idLabel);
            }

            if (this.contact.idType === 'BRN') {
                if (!this.contact.sst_reg_no)
                    missingFields.push('SST Registration Number');
                if (!this.contact.enter_id)
                    missingFields.push('Tax Identification Number (TIN)');
            }

            return missingFields;
        },
    },
};
</script>

<style scoped>
.modern-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: #f8fafc;
}
.modern-container {
    flex: 1;
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
}

.header-section {
    text-align: center;
    padding: 32px 24px;
    margin-bottom: 32px;
}

.header-content {
    max-width: 600px;
    margin: 0 auto;
}

.header-title-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
}

.header-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    color: #ef5350;
    margin-bottom: 0;
}

.header-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0; /* Remove all margins */
    letter-spacing: -0.025em;
}

.header-subtitle {
    font-size: 1rem;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .header-title-container {
        gap: 8px; /* Maintain gap between icon and title */
        flex-direction: row; /* Ensure they stay horizontal */
        align-items: center; /* Keep them vertically centered */
    }

    .header-section {
        padding: 24px 16px;
        margin-bottom: 24px;
    }

    .header-icon {
        width: 36px; /* Slightly smaller on mobile */
        height: 36px;
        border-radius: 8px; /* Adjust border radius proportionally */
    }

    .header-title {
        font-size: 1.25rem; /* Smaller but still readable */
        line-height: 1.2; /* Tighter line height for mobile */
    }

    .header-subtitle {
        font-size: 0.875rem;
        margin-top: 8px; /* Add small margin for better spacing */
    }
}

/* For very small screens */
@media (max-width: 480px) {
    .header-title-container {
        gap: 6px; /* Smaller gap for very small screens */
    }

    .header-icon {
        width: 32px;
        height: 32px;
    }

    .header-title {
        font-size: 1.1rem; /* Even smaller for very small screens */
    }

    .header-subtitle {
        font-size: 0.8rem;
    }
}

.section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
    overflow: hidden;
}
.section-header {
    display: flex;
    align-items: center;
    padding: 20px 24px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
}
.section-icon {
    color: #ef5350;
    margin-right: 12px;
    flex-shrink: 0;
}
.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}
.form-grid {
    display: grid;
    grid-template-columns: 1fr; /* Default for mobile */
    gap: 16px; /* Adjusted gap for tighter layout */
    padding: 24px;
}

/* Specific grid layouts for desktop */
@media (min-width: 768px) {
    .form-grid.personal-info-grid {
        grid-template-columns: 1fr 1fr;
    }
    .form-grid.address-info-grid {
        grid-template-columns: 1fr 1fr 1fr; /* 3 columns for State, Country, Postal Code */
    }
    .form-grid.identification-info-grid {
        grid-template-columns: 1fr; /* Stacked for ID & Tax */
    }
    .form-group.full-width {
        grid-column: 1 / -1; /* Span all columns */
    }
}

.form-group {
    display: flex;
    flex-direction: column;
}
.modern-label {
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
    font-size: 14px;
    display: flex;
    align-items: center;
}
.required {
    color: #ef5350;
    margin-left: 4px;
}
.modern-input {
    padding: 12px 16px;
    border: 1px solid #d1d5db; /* Lighter border as per image */
    border-radius: 6px; /* Slightly less rounded */
    font-size: 16px;
    transition: all 0.2s ease;
    background: white;
    width: 100%;
    box-sizing: border-box;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05); /* Subtle inner shadow */
}
.modern-input:focus {
    outline: none;
    border-color: #3b82f6; /* Blue focus border as per image */
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25); /* Blue focus ring */
}
.modern-input.input-focused {
    border-color: #3b82f6; /* Blue border when focused/filled */
}
.modern-textarea {
    resize: vertical;
    min-height: 80px;
}
.label-with-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}
.info-text {
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
}
.help-text {
    font-size: 12px;
    color: #ef5350;
    margin-top: 6px;
    display: flex;
    align-items: center;
}
.modern-button-group {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 24px;
    background: white;
    border-top: 1px solid #e5e7eb;
    position: sticky;
    bottom: 0;
}
.modern-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 120px;
    justify-content: center;
}
.modern-btn-primary {
    background: #ef5350;
    color: white;
}
.modern-btn-primary:hover {
    background: #d32f2f;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 83, 80, 0.3);
}
.modern-btn-secondary {
    background: white;
    color: #374151;
    border: 2px solid #e5e7eb;
}
.modern-btn-secondary:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
}
/* Mobile Responsive */
@media (max-width: 768px) {
    .modern-container {
        padding: 16px;
    }
    .section-header {
        padding: 16px 20px;
    }
    .form-grid {
        padding: 20px;
        gap: 16px;
    }
    .modern-button-group {
        padding: 16px;
        flex-direction: column;
    }
    .modern-btn {
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/e-invoice/portal/buyer-info.vue